import { memo, type ReactElement, type MouseEvent } from 'react';
import { Alert, Box, IconButton, Stack } from '@mui/material';
import PerfectScrollbar from 'react-perfect-scrollbar';
import { useNavigate } from 'react-router-dom';
import TipsAndUpdatesIcon from '@mui/icons-material/TipsAndUpdates';
import CloseIcon from '@mui/icons-material/Close';
import { Button } from '@travelity/ui';
import { useStepperContext } from '@components/stepper';
import { useHelperContext } from '@contexts/helper-context';
import { PageTitle } from '@components/page-title';

export interface StepContentProps {
    children: ReactElement;
    title: string;
    onClick?: (e: MouseEvent) => void;
    errors: string[];
    doNotDisable?: boolean;
}

const StepContent = ({
    children,
    title,
    onClick,
    errors,
    doNotDisable = true,
}: StepContentProps) => {
    const { prev, activeIndex, setShowErrors, steps } = useStepperContext();
    const navigate = useNavigate();
    const enabled = false;

    const { info, close } = useHelperContext();

    return (
        <>
            <PageTitle
                buttons={
                    <Stack direction="row" spacing={1}>
                        <Button
                            color="neutral"
                            variant="contained"
                            onClick={() =>
                                activeIndex ? prev() : navigate(-1)
                            }
                        >
                            {activeIndex ? 'Back' : 'Cancel'}
                        </Button>

                        <Button
                            color="secondary"
                            variant="contained"
                            type="submit"
                            form="step"
                            onClick={onClick}
                            doNotDisable={doNotDisable}
                            requiredFields={errors}
                            onIconClick={() => setShowErrors(true)}
                        >
                            {activeIndex < steps.length - 1 ? 'Next' : 'Save'}
                        </Button>
                    </Stack>
                }
            >
                {title}
            </PageTitle>
            <Box sx={{ height: 'calc(100% - 60px)' }}>
                <PerfectScrollbar>
                    <Box sx={{ pt: 1, pr: 1, pl: { xs: 1, md: 0 } }}>
                        {children}
                    </Box>
                </PerfectScrollbar>
            </Box>

            {info && enabled && (
                <Alert
                    variant="outlined"
                    severity="info"
                    icon={<TipsAndUpdatesIcon sx={{ color: '#EECC52' }} />}
                    action={
                        <IconButton
                            aria-label="close"
                            color="inherit"
                            size="small"
                            onClick={close}
                        >
                            <CloseIcon fontSize="inherit" />
                        </IconButton>
                    }
                    sx={{
                        width: { xs: '100%', sm: '388px' },
                        position: 'absolute',
                        right: '30px',
                        top: '74px',
                        bgcolor: '#FFF',
                    }}
                >
                    {info}
                </Alert>
            )}
        </>
    );
};

export default memo(StepContent);
